import { useEffect, useState, useCallback } from 'react';
import {
  Plus, Pencil, Trash2, Loader2, Power, PowerOff, Check, CheckCircle2, Clock, Tag,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import adminApi from '../lib/adminApi';

function PromocaoForm({ inicial, anunciantes, onSalvar, onCancelar, salvando }) {
  const [form, setForm] = useState(inicial || {
    titulo: '', descricao: '', precoOriginal: '', precoPromocional: '',
    nomeAnunciante: '', anuncianteId: 'manual', ativa: true, aprovada: true,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSalvar({
          ...form,
          precoOriginal: form.precoOriginal === '' ? null : Number(form.precoOriginal),
          precoPromocional: form.precoPromocional === '' ? null : Number(form.precoPromocional),
        });
      }}
      className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3 mb-4"
    >
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Título</label>
        <input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} required data-testid="promo-titulo-input" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" placeholder="Ex: 30% OFF em tintas" />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Descrição</label>
        <input value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Preço original (R$)</label>
          <input type="number" step="0.01" value={form.precoOriginal} onChange={(e) => setForm({ ...form, precoOriginal: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Preço promo (R$)</label>
          <input type="number" step="0.01" value={form.precoPromocional} onChange={(e) => setForm({ ...form, precoPromocional: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Anunciante</label>
        <select value={form.anuncianteId || 'manual'} onChange={(e) => {
          const v = e.target.value;
          const a = anunciantes.find(x => x.id === v);
          setForm({ ...form, anuncianteId: v, nomeAnunciante: a ? a.nome : form.nomeAnunciante });
        }} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]">
          <option value="manual">— Manual —</option>
          {anunciantes.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
        </select>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Nome exibido (ticker)</label>
        <input value={form.nomeAnunciante} onChange={(e) => setForm({ ...form, nomeAnunciante: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" placeholder="Ex: Casa das Tintas" />
      </div>

      <div className="flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.ativa} onChange={(e) => setForm({ ...form, ativa: e.target.checked })} className="w-4 h-4 accent-[#1D9E75]" />
          <span>Ativa</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.aprovada} onChange={(e) => setForm({ ...form, aprovada: e.target.checked })} className="w-4 h-4 accent-[#1D9E75]" />
          <span>Aprovada</span>
        </label>
      </div>

      <div className="flex gap-2 pt-1">
        <button type="submit" disabled={salvando} data-testid="salvar-promo-btn" className="flex-1 bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl py-2 text-sm disabled:opacity-50 flex items-center justify-center gap-2">
          {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar
        </button>
        <button type="button" onClick={onCancelar} className="px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl py-2 text-sm">Cancelar</button>
      </div>
    </form>
  );
}

export default function AdminPromocoes() {
  const [lista, setLista] = useState([]);
  const [anunciantes, setAnunciantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        adminApi.get('/admin/promocoes'),
        adminApi.get('/admin/anunciantes'),
      ]);
      setLista(p.data);
      setAnunciantes(a.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const handleCriar = async (form) => {
    setSalvando(true);
    try { await adminApi.post('/admin/promocoes', form); setCriando(false); carregar(); }
    catch (err) { alert(err?.response?.data?.detail || 'Erro'); }
    finally { setSalvando(false); }
  };

  const handleAtualizar = async (form) => {
    setSalvando(true);
    try { await adminApi.patch(`/admin/promocoes/${editando.id}`, form); setEditando(null); carregar(); }
    catch (err) { alert(err?.response?.data?.detail || 'Erro'); }
    finally { setSalvando(false); }
  };

  const toggle = async (p, campo) => {
    await adminApi.patch(`/admin/promocoes/${p.id}`, { [campo]: !p[campo] });
    carregar();
  };

  const excluir = async (p) => {
    if (!window.confirm(`Excluir promoção "${p.titulo}"?`)) return;
    await adminApi.delete(`/admin/promocoes/${p.id}`);
    carregar();
  };

  return (
    <AdminLayout title="Promoções">
      {!criando && !editando && (
        <button onClick={() => setCriando(true)} data-testid="nova-promo-btn" className="mb-4 bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl px-4 py-2.5 text-sm flex items-center gap-2">
          <Plus size={15} /> Nova promoção
        </button>
      )}

      {criando && <PromocaoForm anunciantes={anunciantes} onSalvar={handleCriar} onCancelar={() => setCriando(false)} salvando={salvando} />}
      {editando && <PromocaoForm inicial={editando} anunciantes={anunciantes} onSalvar={handleAtualizar} onCancelar={() => setEditando(null)} salvando={salvando} />}

      {loading ? (
        <div className="py-12 text-center text-gray-400 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Carregando...
        </div>
      ) : lista.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
          <Tag size={28} className="text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Nenhuma promoção cadastrada.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
          {lista.map((p) => (
            <div key={p.id} data-testid={`promo-${p.id}`} className="p-4 hover:bg-gray-50 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold truncate ${p.ativa && p.aprovada ? 'text-gray-900' : 'text-gray-400'}`}>{p.titulo}</p>
                <p className="text-xs text-gray-500 truncate">{p.descricao || '—'}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs">
                  {p.precoOriginal != null && p.precoPromocional != null && (
                    <span>
                      <span className="text-gray-400 line-through">R$ {Number(p.precoOriginal).toFixed(2)}</span>
                      <span className="text-[#1D9E75] font-semibold ml-2">R$ {Number(p.precoPromocional).toFixed(2)}</span>
                    </span>
                  )}
                  {p.nomeAnunciante && <span className="text-gray-500">· {p.nomeAnunciante}</span>}
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button onClick={() => toggle(p, 'aprovada')} title={p.aprovada ? 'Reprovar' : 'Aprovar'} data-testid={`aprovar-promo-${p.id}`} className={`p-1.5 rounded-lg ${p.aprovada ? 'text-[#1D9E75] hover:bg-[#E1F5EE]' : 'text-amber-500 hover:bg-amber-50'}`}>
                  {p.aprovada ? <CheckCircle2 size={14} /> : <Clock size={14} />}
                </button>
                <button onClick={() => toggle(p, 'ativa')} title={p.ativa ? 'Desativar' : 'Ativar'} className={`p-1.5 rounded-lg ${p.ativa ? 'text-[#1D9E75] hover:bg-[#E1F5EE]' : 'text-gray-400 hover:bg-gray-100'}`}>
                  {p.ativa ? <Power size={14} /> : <PowerOff size={14} />}
                </button>
                <button onClick={() => setEditando(p)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><Pencil size={14} /></button>
                <button onClick={() => excluir(p)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
